import React, { useEffect, useState } from "react";
import { FiX, FiEye, FiEyeOff } from "react-icons/fi";
import Select from "react-select";
import Swal from "sweetalert2";
import { Loading } from "../../../../../../components/Loading/index.jsx";
import { BrComerceApiService } from "../../../../../../services/BrComerceApiService.js";
import styles from "./styles.module.scss";

const PartnerModal = ({ partner, onClose }) => {
  const api = new BrComerceApiService();
  const isEditing = !!partner;

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    protocol: "sftp",
    parser: "proceda",
    host: "",
    port: 22,
    username: "",
    password: "",
    folderNotfis: "",
    folderOcoren: "",
    folderAttachments: "",
    folderProcessed: "",
    environment: "PROD",
    isActive: true
  });

  const [errors, setErrors] = useState({});

  const environmentOptions = [
    { value: "PROD", label: "Produção" },
    { value: "HML", label: "Homologação" }
  ];

  const protocolOptions = [
    { value: "sftp", label: "SFTP" },
    { value: "ftp", label: "FTP" }
  ];

  const parserOptions = [
    { value: "proceda", label: "Proceda 3.0" },
    { value: "intelipost", label: "Intelipost 3.1" }
  ];

  useEffect(() => {
    if (partner) {
      setFormData({
        name: partner.name || "",
        code: partner.code || "",
        protocol: partner.protocol || "sftp",
        parser: partner.parser || "proceda",
        host: partner.host || "",
        port: partner.port || 22,
        username: partner.username || "",
        password: partner.password || "",
        folderNotfis: partner.folder_notfis || "",
        folderOcoren: partner.folder_ocoren || "",
        folderAttachments: partner.folder_attachments || "",
        folderProcessed: partner.folder_processed || "",
        environment: partner.environment || "PROD",
        isActive: partner.is_active !== undefined ? partner.is_active : true
      });
    }
  }, [partner]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));

    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleProtocolChange = (opt) => {
    const newProtocol = opt.value;
    setFormData((prev) => ({
      ...prev,
      protocol: newProtocol,
      port: newProtocol === "ftp" ? 21 : 22
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Nome é obrigatório";
    }

    if (!formData.code.trim()) {
      newErrors.code = "Código é obrigatório";
    }

    if (!formData.host.trim()) {
      newErrors.host = "Host é obrigatório";
    }

    if (!formData.port || formData.port < 1) {
      newErrors.port = "Porta inválida";
    }

    if (!formData.username.trim()) {
      newErrors.username = "Usuário é obrigatório";
    }

    if (!isEditing && !formData.password.trim()) {
      newErrors.password = "Senha é obrigatória";
    }

    if (!formData.folderNotfis.trim()) {
      newErrors.folderNotfis = "Pasta de notificações é obrigatória";
    }

    if (!formData.folderOcoren.trim()) {
      newErrors.folderOcoren = "Pasta de ocorrências é obrigatória";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const dataToSend = {
        name: formData.name.trim(),
        code: formData.code.trim(),
        protocol: formData.protocol,
        parser: formData.parser,
        host: formData.host.trim(),
        port: parseInt(formData.port),
        username: formData.username.trim(),
        folder_notfis: formData.folderNotfis.trim(),
        folder_ocoren: formData.folderOcoren.trim(),
        folder_attachments: formData.folderAttachments.trim() || null,
        folder_processed: formData.folderProcessed.trim() || null,
        environment: formData.environment,
        is_active: formData.isActive
      };

      // Só envia senha se foi preenchida
      if (formData.password.trim()) {
        dataToSend.password = formData.password.trim();
      }

      if (isEditing) {
        await api.makeHttpRequest({
          method: "PUT",
          url: `/proceda-partners/${partner.id}`,
          data: dataToSend
        });

        Swal.fire({
          title: "Sucesso!",
          text: "Parceiro atualizado com sucesso.",
          icon: "success",
          confirmButtonText: "Ok"
        });
      } else {
        await api.makeHttpRequest({
          method: "POST",
          url: `/proceda-partners`,
          data: dataToSend
        });

        Swal.fire({
          title: "Sucesso!",
          text: "Parceiro criado com sucesso.",
          icon: "success",
          confirmButtonText: "Ok"
        });
      }

      onClose(true);
    } catch (error) {
      console.error("Error saving partner:", error);

      let errorMessage = "Não foi possível salvar o parceiro.";

      if (error?.response?.status === 409) {
        errorMessage = "Já existe um parceiro com este código.";
      }

      Swal.fire({
        icon: "error",
        title: "Erro!",
        text: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <>
      <Loading loading={loading} />
      <div className={styles.modalOverlay} onClick={handleOverlayClick}>
        <div className={styles.modal}>
          <div className={styles.modalHeader}>
            <h3>{isEditing ? "Editar Parceiro" : "Novo Parceiro"}</h3>
            <button
              className={styles.closeButton}
              onClick={() => onClose(false)}
              type="button"
            >
              <FiX size={24} />
            </button>
          </div>

          <form
            onSubmit={handleSubmit}
            className={styles.modalBody}
            autoComplete="off"
          >
            {/* Dummy fields para impedir autofill do navegador em campos sensíveis */}
            <input
              type="text"
              name="prevent-autofill-username"
              autoComplete="username"
              style={{ display: "none" }}
              tabIndex={-1}
              aria-hidden="true"
            />
            <input
              type="password"
              name="prevent-autofill-password"
              autoComplete="new-password"
              style={{ display: "none" }}
              tabIndex={-1}
              aria-hidden="true"
            />
            <div className={styles.formSection}>
              <h4>Informações Básicas</h4>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>
                    Nome do Parceiro <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    placeholder="Digite o nome do parceiro"
                    className={errors.name ? styles.inputError : ""}
                    autoComplete="off"
                  />
                  {errors.name && (
                    <span className={styles.errorMessage}>{errors.name}</span>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label>
                    Código do Parceiro <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => handleInputChange("code", e.target.value)}
                    placeholder="Digite o código único"
                    className={errors.code ? styles.inputError : ""}
                    autoComplete="off"
                  />
                  {errors.code && (
                    <span className={styles.errorMessage}>{errors.code}</span>
                  )}
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>
                    Ambiente <span className={styles.required}>*</span>
                  </label>
                  <Select
                    value={environmentOptions.find(
                      (opt) => opt.value === formData.environment
                    )}
                    onChange={(opt) => handleInputChange("environment", opt.value)}
                    options={environmentOptions}
                    placeholder="Selecione o ambiente"
                    classNamePrefix="react-select"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Status</label>
                  <div className={styles.switchContainer}>
                    <label className={styles.switch}>
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) =>
                          handleInputChange("isActive", e.target.checked)
                        }
                      />
                      <span className={`${styles.slider} ${styles.round}`}></span>
                    </label>
                    <span className={styles.switchLabel}>
                      {formData.isActive ? "Ativo" : "Inativo"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.formSection}>
              <h4>Configurações de Conexão</h4>

              <div className={styles.formRow}>
                <div className={styles.formGroup} style={{ flex: "0 0 200px" }}>
                  <label>
                    Protocolo <span className={styles.required}>*</span>
                  </label>
                  <Select
                    value={protocolOptions.find(
                      (opt) => opt.value === formData.protocol
                    )}
                    onChange={handleProtocolChange}
                    options={protocolOptions}
                    placeholder="Selecione o protocolo"
                    classNamePrefix="react-select"
                  />
                </div>

                <div className={styles.formGroup} style={{ flex: "0 0 220px" }}>
                  <label>
                    Layout (Parser) <span className={styles.required}>*</span>
                  </label>
                  <Select
                    value={parserOptions.find(
                      (opt) => opt.value === formData.parser
                    )}
                    onChange={(opt) => handleInputChange("parser", opt.value)}
                    options={parserOptions}
                    placeholder="Selecione o layout"
                    classNamePrefix="react-select"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>
                    Host <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.host}
                    onChange={(e) => handleInputChange("host", e.target.value)}
                    placeholder={formData.protocol === "ftp" ? "ftp.example.com" : "sftp.example.com"}
                    className={errors.host ? styles.inputError : ""}
                    autoComplete="off"
                  />
                  {errors.host && (
                    <span className={styles.errorMessage}>{errors.host}</span>
                  )}
                </div>

                <div className={styles.formGroup} style={{ flex: "0 0 150px" }}>
                  <label>
                    Porta <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.port}
                    onChange={(e) =>
                      handleInputChange("port", e.target.value)
                    }
                    placeholder={formData.protocol === "ftp" ? "21" : "22"}
                    min="1"
                    max="65535"
                    className={errors.port ? styles.inputError : ""}
                    autoComplete="off"
                  />
                  {errors.port && (
                    <span className={styles.errorMessage}>{errors.port}</span>
                  )}
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>
                    Usuário <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) =>
                      handleInputChange("username", e.target.value)
                    }
                    placeholder="Digite o usuário"
                    className={errors.username ? styles.inputError : ""}
                    autoComplete="off"
                    name="proceda-partner-username"
                    spellCheck={false}
                    autoCorrect="off"
                    autoCapitalize="off"
                  />
                  {errors.username && (
                    <span className={styles.errorMessage}>
                      {errors.username}
                    </span>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label>
                    Senha{" "}
                    {!isEditing && <span className={styles.required}>*</span>}
                    {isEditing && (
                      <span className={styles.helpText}>
                        (deixe em branco para não alterar)
                      </span>
                    )}
                  </label>
                  <div className={styles.passwordInputContainer}>
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) =>
                        handleInputChange("password", e.target.value)
                      }
                      placeholder={isEditing ? "••••••••" : "Digite a senha"}
                      className={errors.password ? styles.inputError : ""}
                      autoComplete="new-password"
                      name="proceda-partner-password"
                      spellCheck={false}
                    />
                    <button
                      type="button"
                      className={styles.passwordToggle}
                      onClick={togglePasswordVisibility}
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <FiEyeOff size={18} />
                      ) : (
                        <FiEye size={18} />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <span className={styles.errorMessage}>
                      {errors.password}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.formSection}>
              <h4>Pastas no servidor</h4>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>
                    Pasta de Notfis <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.folderNotfis}
                    onChange={(e) =>
                      handleInputChange("folderNotfis", e.target.value)
                    }
                    placeholder="/NOTFIS"
                    className={errors.folderNotfis ? styles.inputError : ""}
                    autoComplete="off"
                  />
                  {errors.folderNotfis && (
                    <span className={styles.errorMessage}>
                      {errors.folderNotfis}
                    </span>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label>
                    Pasta de Ocoren <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.folderOcoren}
                    onChange={(e) =>
                      handleInputChange("folderOcoren", e.target.value)
                    }
                    placeholder="/OCOREN"
                    className={errors.folderOcoren ? styles.inputError : ""}
                    autoComplete="off"
                  />
                  {errors.folderOcoren && (
                    <span className={styles.errorMessage}>
                      {errors.folderOcoren}
                    </span>
                  )}
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>
                    Pasta de Anexos{" "}
                    <span className={styles.helpText}>(opcional)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.folderAttachments}
                    onChange={(e) =>
                      handleInputChange("folderAttachments", e.target.value)
                    }
                    placeholder="/ATTACHMENTS"
                    autoComplete="off"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>
                    Pasta de Processados{" "}
                    <span className={styles.helpText}>(opcional)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.folderProcessed}
                    onChange={(e) =>
                      handleInputChange("folderProcessed", e.target.value)
                    }
                    placeholder="/PROCESSED"
                    autoComplete="off"
                  />
                </div>
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button
                type="button"
                className={styles.buttonCancel}
                onClick={() => onClose(false)}
              >
                Cancelar
              </button>
              <button type="submit" className={styles.buttonSave}>
                {isEditing ? "Salvar Alterações" : "Criar Parceiro"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default PartnerModal;
